import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { ContractCondition } from '~/typings/entities/ContractCondition';

import { computeChangedFields } from './conditionData';

type ConditionContextValue = {
  conditions: ContractCondition[];
  /**
   * CCB do proprio contrato, independente de refinanciamento. Sempre presente, ao contrario de
   * `selectedCondition`, que so existe quando ha versoes de refin.
   */
  baseCcb?: string;
  /**
   * As condicoes ainda estao sendo buscadas. Quem consome precisa distinguir "ainda nao sei" de
   * "nao tem": sem isso, `baseCcb` indefinido durante a busca e lido como contrato sem CCB.
   */
  loading: boolean;
  selectedCondition?: ContractCondition;
  currentCondition?: ContractCondition;
  isViewingCurrent: boolean;
  changedFields: string[];
  selectCondition: (ccb: string) => void;
  backToCurrent: () => void;
};

const ConditionContext = createContext<ConditionContextValue>({
  conditions: [],
  baseCcb: undefined,
  loading: false,
  isViewingCurrent: true,
  changedFields: [],
  selectCondition: () => {},
  backToCurrent: () => {},
});

type ConditionProviderProps = {
  conditions: ContractCondition[];
  baseCcb?: string;
  loading?: boolean;
  children: ReactNode;
};

export function ConditionProvider({ conditions, baseCcb, loading = false, children }: ConditionProviderProps) {
  const [selectedCcb, setSelectedCcb] = useState<string | null>(null);

  const currentCondition = useMemo(() => conditions.find((c) => c.current) || conditions[0], [conditions]);

  const selectedCondition = useMemo(
    () => (selectedCcb ? conditions.find((c) => c.ccb === selectedCcb) : undefined) || currentCondition,
    [conditions, selectedCcb, currentCondition],
  );

  const previousCondition = useMemo(
    () => (selectedCondition ? conditions.find((c) => c.version === selectedCondition.version - 1) : undefined),
    [conditions, selectedCondition],
  );

  const changedFields = useMemo(
    () => computeChangedFields(selectedCondition, previousCondition),
    [selectedCondition, previousCondition],
  );

  const selectCondition = useCallback((ccb: string) => setSelectedCcb(ccb), []);
  const backToCurrent = useCallback(() => setSelectedCcb(null), []);

  const value = useMemo(
    () => ({
      conditions,
      baseCcb,
      loading,
      selectedCondition,
      currentCondition,
      isViewingCurrent: !selectedCondition || selectedCondition.ccb === currentCondition?.ccb,
      changedFields,
      selectCondition,
      backToCurrent,
    }),
    [conditions, baseCcb, loading, selectedCondition, currentCondition, changedFields, selectCondition, backToCurrent],
  );

  return <ConditionContext.Provider value={value}>{children}</ConditionContext.Provider>;
}

export const useContractCondition = () => useContext(ConditionContext);
